import * as api from "../../lib/api.js";
import { useApp, type AppState } from "../state/store";

/**
 * "How MeetFolks uses AI" (#915, Apple 5.1.2): the one-time agreement before a meeting.
 *
 * The server keeps the agreement per account and wording version, and reports it on
 * `AccountOut.ai_consent_*`. It always refuses create and join with 403
 * `ai_consent_required` until the account agrees, and always reports
 * `features.ai_consent` on `/v1/config`; an older server without the field shows no sheet.
 */

/** What decides whether the sheet is up. */
export type AiConsentState = Pick<
  AppState,
  "account" | "serverConfig" | "aiConsentDemand" | "session" | "recording" | "settingUp" | "stopping"
>;

/** Whether this deployment asks for the agreement: `features.ai_consent`, absent meaning no. */
export function aiConsentFeatureOn(config: AppState["serverConfig"]): boolean {
  return config?.features?.ai_consent === true;
}

/**
 * Whether the sheet covers the app.
 *
 * Only for somebody signed in, since agreeing is an account's act, and never on the public
 * account-deletion page, which must stay usable without agreeing to anything. A refusal
 * from the server shows it whatever the feature said at boot (the switch may have turned on
 * since). Otherwise it shows on the feature and an account that has not agreed to the
 * current wording -- `=== false`, so an account body without the field asks nothing -- and
 * never over a meeting this device is in, which the server lets carry on.
 */
export function aiConsentShown(state: AiConsentState, path: string): boolean {
  if (!state.account || path === "/delete-account") return false;
  if (state.aiConsentDemand) return true;
  if (state.session || state.recording || state.settingUp || state.stopping) return false;
  return aiConsentFeatureOn(state.serverConfig) && state.account.ai_consent_current === false;
}

/** The wording version to agree to: the newer of the account's and a refusal's. */
export function aiConsentVersion(state: Pick<AppState, "account" | "aiConsentDemand">): number {
  return Math.max(state.account?.ai_consent_version ?? 1, state.aiConsentDemand?.version ?? 1);
}

/** A create or join refused for want of the agreement: its version, or null when it is not one. */
export function aiConsentRefusal(err: unknown): { version: number | null } | null {
  const failure = err as { status?: unknown; data?: { code?: unknown; version?: unknown } } | null;
  if (failure?.status !== 403 || failure.data?.code !== "ai_consent_required") return null;
  const version = failure.data.version;
  return { version: typeof version === "number" ? version : null };
}

/**
 * Ask for the agreement after a refusal, and read the account again behind it, so the
 * sheet names the wording the server wants now. A failed read keeps the refusal's version.
 */
export function demandAiConsent(
  version: number | null,
  retry: NonNullable<AppState["aiConsentDemand"]>["retry"],
  chirp: boolean
): void {
  useApp.setState({ aiConsentDemand: { version, retry, chirp } });
  void readAccount();
}

/** The account again, into the store; false when it could not be read. */
async function readAccount(): Promise<boolean> {
  try {
    const account = await api.account();
    if (account) useApp.getState().setAccount(account);
    return account !== null;
  } catch {
    return false;
  }
}

/**
 * How Agree went.
 *
 * - `agreed`: recorded; the store holds the account, now current.
 * - `stale`: the wording changed since it was shown; the store now holds the current one.
 * - `failed`: nothing recorded; the sheet says so and offers Agree again.
 */
export type AgreeOutcome = "agreed" | "stale" | "failed";

/** POST the agreement to `version`, and settle the store for whichever way it went. */
export async function agreeToAiUse(version: number): Promise<AgreeOutcome> {
  try {
    const account = await api.agreeToAiUse(version);
    useApp.getState().setAccount(account);
    return "agreed";
  } catch (err) {
    const failure = err as { status?: unknown; data?: { code?: unknown; version?: unknown } } | null;
    if (failure?.status !== 409 || failure.data?.code !== "ai_consent_version_stale") return "failed";
    if (await readAccount()) return "stale";
    // The account could not be read again: the refusal's own version is the current one,
    // so the next Agree names it instead of the one just refused.
    const current = failure.data.version;
    const account = useApp.getState().account;
    if (typeof current !== "number" || !account) return "failed";
    useApp.getState().setAccount({ ...account, ai_consent_version: current, ai_consent_current: false });
    return "stale";
  }
}
